import { Injectable, Optional } from '@angular/core';
import Keycloak from 'keycloak-js';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import { LocalStorageService } from './local-storage.service';

const REDIRECT_KEY = 'postLoginRedirect';
// Why the user was signed out, shown after Keycloak's logout redirect lands
// the app back here. sessionStorage because it must survive that full-page
// redirect but not leak into other tabs or a later visit.
const AUTH_NOTICE_KEY = 'authNotice';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public isUserDataReceived = new Subject<boolean>();
  public isUserDataReceived$ = this.isUserDataReceived.asObservable();

  // Keycloak is OPTIONAL: with environment.authEnabled=false no Keycloak
  // provider is registered at all, and a required dependency would make the
  // whole app fail to bootstrap. Every use below is guarded.
  constructor(
    @Optional() private keycloak: Keycloak | null,
    private router: Router,
    private ls: LocalStorageService,
  ) {}

  // App-owned localStorage keys cleared on logout. `agRealm` is
  // intentionally OMITTED — it's the user's tenant choice (agents vs
  // admins) and it's read at app boot to seed the keycloak realm
  // config (see app.config.ts). Wiping it before login would silently
  // flip the user to the default 'agents' realm.
  private static readonly APP_LS_KEYS = [
    'agency',
    'agencyID',
    'consumer',
    'notificationMessage',
    'selectedRealm',
  ];

  logout() {
    // Previous implementation:
    //
    //   this.router.navigate(['/']).then(() => {
    //     this.ls.clear();
    //     this.keycloak.logout();
    //   });
    //
    // Three lined-up problems that together produced the
    // "first-click empty-error, second-click works" symptom:
    //
    //   1) router.navigate(['/']).then(...) — when the user clicked
    //      logout from /search?q=..., the navigation was racing the
    //      ongoing getSessionData / Keycloak token-refresh activity
    //      on that route. If the navigation promise rejected (route
    //      guard tripped, auth state in flux), the .then() never
    //      fired and logout was a no-op. The toastr handler that
    //      caught the rejected promise showed an empty " " message
    //      because the rejection reason was undefined.
    //
    //   2) localStorage.clear() — nukes EVERYTHING, including
    //      keycloak-js's own persistence keys (kc-callback-*,
    //      kc-state-*) AND `agRealm`. With agRealm gone the next
    //      sign-in falls back to the default 'agents' realm regardless
    //      of where the user came from. With kc-* gone, keycloak's
    //      logout may compute a malformed end-session URL.
    //
    //   3) keycloak.logout() returns a Promise that was unhandled.
    //      Any rejection bubbled up as an unhandled rejection and
    //      Sentry's global handler surfaced it as the empty toastr.
    //
    // The fix:
    //
    //   - Clear ONLY the app-owned keys (APP_LS_KEYS), preserving
    //     agRealm and any kc-* entries keycloak-js relies on.
    //   - Drop the router.navigate — keycloak.logout() redirects via
    //     window.location to the Keycloak end-session endpoint, which
    //     in turn redirects back to the configured post-logout URL.
    //     The manual navigate was always redundant and created the
    //     race.
    //   - Catch the logout promise so a rejection becomes a real,
    //     visible error instead of an empty toast.
    for (const key of AuthService.APP_LS_KEYS) {
      this.ls.removeItem(key);
    }

    this.keycloak?.logout().catch((err: unknown) => {
      // We can't recover from a failed logout here — the only sane
      // fallback is to drop the user back at / with their session
      // still active. Log to the console so the failure is at least
      // diagnosable in DevTools.
      // eslint-disable-next-line no-console
      console.error('Keycloak logout failed', err);
      this.router.navigate(['/']);
    });
  }

  // Set once a rejected session has started the logout redirect. Several
  // requests are usually in flight when the first 401 lands (list, counters,
  // version) and each will fail the same way; only the first may act.
  private endingSession = false;

  /**
   * End the session because the server refused it.
   *
   * This is a full Keycloak logout, not a local reset, on purpose: the SSO
   * session is still valid, so a local reset followed by "Log in" would
   * silently sign the SAME rejected identity straight back in. Ending the SSO
   * session makes the next login ask for credentials, so the user can switch
   * to an account that works.
   */
  endRejectedSession(notice: string): void {
    if (this.endingSession) return;
    this.endingSession = true;
    sessionStorage.setItem(AUTH_NOTICE_KEY, notice);
    this.logout();
  }

  /** The notice left by endRejectedSession, if any. Read once, then cleared. */
  consumeAuthNotice(): string | null {
    const notice = sessionStorage.getItem(AUTH_NOTICE_KEY);
    if (notice) sessionStorage.removeItem(AUTH_NOTICE_KEY);
    return notice;
  }

  saveRedirectedUrl(url: string): void {
    if (url !== '/') {
      sessionStorage.setItem(REDIRECT_KEY, url);
    }
  }

  consumeRedirectedUrl(): string | null {
    const url = sessionStorage.getItem(REDIRECT_KEY);
    if (url && this.keycloak?.authenticated) {
      sessionStorage.removeItem(REDIRECT_KEY);
    }
    return url;
  }
}
